import React, { useCallback, useEffect, useMemo, useState } from 'react';

import FilesList from '@components/common/FilesList';
import Modal from '@components/common/Modal';
import UploadFile from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile';
import ReplaceConfirm
	from '@components/routes/CmModule/ConfigurationDataModelManagement/AttachSchemaModel/ReplaceConfirm';

import { useCreateOranYangFiles } from '@hooks/configurationDataModelManagement/useCreateOranYangFiles';
import { useMutateSchemaUpload } from '@hooks/configurationDataModelManagement/useMutateSchemaUpload';

import { IAnalysisSchemaUrl } from '@interfaces/cmModule/schemaConfig';

import { EMIMETypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { fileExtensions } from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';
import {
	acceptedFileTypesYangSchema,
	fileErrorsYangSchema,
} from '@components/routes/CmModule/ConfigurationDataModelManagement/constants';

import styles from './AttachYangFiles.module.scss';

const MAX_FILES = 20;

interface IAttachSchemaConfig {
	onClose: () => void;
	importYangName: string;
	setIsConfirmYangModalNeeded: React.Dispatch<React.SetStateAction<boolean>>;
}

const AttachYangFiles = ({
	onClose,
	importYangName,
	setIsConfirmYangModalNeeded,
}: IAttachSchemaConfig) => {
	const [fileNames, setFileNames] = useState<string[]>([]);
	const [importIds, setImportIds] = useState<string[]>([]);
	const [analyzedConfigs, setAnalyzedConfigs] = useState<IAnalysisSchemaUrl[]>([]);
	const [isConfirmModals, setIsConfirmModals] = useState<boolean>(false);
	const [errorAnalysisSchema, setErrorAnalysisSchema] = useState<string | undefined>();

	const existingFiles = useMemo(() => {
		return analyzedConfigs
			?.flatMap((config) => config.items)
			.filter((item) => !item?.isFileAnalyzed);
	}, [analyzedConfigs]);

	const { createUploadUrlAttachment, isUploadingFile } = useMutateSchemaUpload({
		setImportIds,
		setAnalyzedConfigs,
		setIsConfirmModals,
		formValues: { name: importYangName },
		setErrorAnalysisSchema,
		isOranVendor: true,
	});

	const {
		createOranYangFiles,
		isLoadingCreateOranYangFiles,
	} = useCreateOranYangFiles({
		onClose,
	});

	const onProceedClickHandler = useCallback(() => {
		if (!importIds?.length) return;

		if (existingFiles?.length) {
			setIsConfirmModals(true);
			return;
		}

		if (analyzedConfigs.length) {
			const preparedData = analyzedConfigs.map((config) => ({
				importId: config.importId || '',
				isReplaceAllDuplicates: config.isReplaceAllDuplicates,
				isSkipAllDuplicates: config.isSkipAllDuplicates,
				...(!(config.isReplaceAllDuplicates || config.isSkipAllDuplicates)
					? {
						duplicateDecisionResults: config.items.map((item) => ({
							id: item.id,
							isReplace: !!item.isReplace,
						})),
					}
					: {}),
			}));

			createOranYangFiles(preparedData);
			setIsConfirmYangModalNeeded(false);
		}
	}, [analyzedConfigs, createOranYangFiles, existingFiles, importIds, setIsConfirmYangModalNeeded]);

	useEffect(() => {
		if (analyzedConfigs.length) {
			setIsConfirmYangModalNeeded(true);
		}
	}, [analyzedConfigs.length, setIsConfirmYangModalNeeded]);

	const onCloseReplaceConfirms = useCallback(() => {
		setIsConfirmModals(false);
	}, []);

	const preparedFilesList = useMemo(() => {
		return analyzedConfigs
			.filter((config) => {

				if (config.isSkipAllDuplicates) return false;

				return !(config.items.length > 0
					&& config.items[0].isReplace !== undefined
					&& !config.items[0].isReplace);
			})
			.map((config) => ({
				name: config.file?.name || '',
				id: config.importId || '',
				size: config?.file?.size || 0,
				type: {
					title: fileExtensions[EMIMETypes.yang],
				},
				mimeType: EMIMETypes.yang,
			}));
	}, [analyzedConfigs]);

	const handleRemoveFile = useCallback((id: string) => {
		setAnalyzedConfigs((prevState) => {
			return prevState.filter((config) => config.importId !== id);
		});
	}, [setAnalyzedConfigs]);

	const isLoadingUploadFile = isUploadingFile || isLoadingCreateOranYangFiles;

	const calculatedMaxFiles = Math.max(0, MAX_FILES - preparedFilesList.length);

	return (
		<div>
			<Modal
				isOpen={!!existingFiles?.length && isConfirmModals}
				onClose={onCloseReplaceConfirms}
				width={MODAL_WIDTH_480}
			>
				<ReplaceConfirm
					existingFiles={existingFiles}
					isSingleFile={existingFiles.length === 1}
					setAnalyzedConfigs={setAnalyzedConfigs}
				/>
			</Modal>
			<UploadFile
				acceptedFileTypes={acceptedFileTypesYangSchema}
				chooseFileDescription='or drag here the YANG files'
				createUploadUrl={createUploadUrlAttachment}
				disabled={!calculatedMaxFiles}
				disabledNext={preparedFilesList.length === 0}
				errorMessage={errorAnalysisSchema}
				errors={fileErrorsYangSchema}
				fileNames={fileNames}
				importIds={importIds}
				importType={EImportingDataType.polygon}
				isDisabledNext={!!errorAnalysisSchema}
				isUploadingFile={isLoadingUploadFile}
				maxFiles={calculatedMaxFiles}
				nextButtonText='Proceed'
				onClose={onClose}
				setCurrentComponent={onProceedClickHandler}
				setFileNames={setFileNames}
				setImportIds={setImportIds}
			>
				{!!preparedFilesList.length &&
					<div className={styles.uploadedMessage}>
						Your files have been uploaded. The files will be parsed using the character set
						UTF-8 {preparedFilesList.length}/{MAX_FILES}
					</div>
				}
				<FilesList
					files={preparedFilesList}
					handleRemoveFile={handleRemoveFile}
				/>
			</UploadFile>
		</div>
	);
};

export default AttachYangFiles;
